import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiBook, FiSearch, FiFileText, FiTag, FiChevronRight, FiArrowLeft } from 'react-icons/fi';
import { kbArticles } from '../data/knowledgeBase';

const KnowledgeBase = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);

  const filtered = kbArticles.filter(a => 
    a.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    a.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto h-full flex flex-col">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800">Knowledge Base</h1>
          <p className="text-xs md:text-sm text-slate-500">Official documentation and troubleshooting guides</p>
        </div>
        <div className="relative w-full md:w-72 shrink-0">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search documentation..." 
            className="w-full pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 flex flex-col md:flex-row gap-4 md:gap-8 min-h-0">
        {/* Article List */}
        <div className={`w-full md:w-1/3 shrink-0 overflow-y-auto space-y-2 md:pr-2 ${selectedArticle ? 'hidden md:block' : 'block'}`}>
          {filtered.map(article => (
            <button 
              key={article.id}
              onClick={() => setSelectedArticle(article)}
              className={`w-full text-left p-4 rounded-xl border transition-all ${
                selectedArticle?.id === article.id 
                  ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200' 
                  : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0 pr-2">
                  <SafeIcon icon={FiFileText} className={`shrink-0 ${selectedArticle?.id === article.id ? 'text-blue-200' : 'text-slate-400'}`} />
                  <span className="text-sm font-bold truncate">{article.title}</span>
                </div>
                <SafeIcon icon={FiChevronRight} className="shrink-0 opacity-50" />
              </div>
              <div className="mt-2 flex items-center space-x-2">
                <span className={`text-[10px] uppercase font-black px-1.5 py-0.5 rounded shrink-0 ${
                  selectedArticle?.id === article.id ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  {article.category}
                </span>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">
              No articles found matching your search.
            </div>
          )}
        </div>

        {/* Article View */}
        <div className={`flex-1 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-8 overflow-y-auto ${!selectedArticle ? 'hidden md:flex md:flex-col md:items-center md:justify-center' : 'block'}`}>
          {selectedArticle ? (
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">
              <button 
                onClick={() => setSelectedArticle(null)}
                className="md:hidden flex items-center space-x-2 text-slate-500 hover:text-blue-600 mb-6 text-xs font-black uppercase tracking-widest bg-slate-50 px-3 py-2 rounded-lg border border-slate-200 w-fit"
              >
                <SafeIcon icon={FiArrowLeft} />
                <span>Back to Articles</span>
              </button>

              <div className="flex items-center space-x-2 text-blue-600 mb-2">
                <SafeIcon icon={FiTag} className="text-sm" />
                <span className="text-xs font-bold uppercase tracking-widest">{selectedArticle.category}</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-slate-800 mb-6">{selectedArticle.title}</h2>
              <div className="prose prose-slate max-w-none">
                <p className="text-sm md:text-base text-slate-600 leading-relaxed whitespace-pre-line">
                  {selectedArticle.content}
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 py-12 md:py-0">
              <SafeIcon icon={FiBook} className="text-5xl md:text-6xl mb-4 opacity-20" />
              <p className="font-medium text-sm md:text-base">Select an article to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBase;